import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Store, BarChart3, Shield, Zap, ChevronDown, ChevronUp, Star, MapPin, Wallet, Navigation } from 'lucide-react';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';

import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import blogService, { PublicBlog } from '../../services/user/blogService';
import testimonialService, { PublicTestimonial } from '../../services/user/testimonialService';
import { getImageUrl } from '../../utils/imageHelper';

const Home: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [featuresRef, featuresVisible] = useScrollAnimation();
  const [stepsRef, stepsVisible] = useScrollAnimation();
  const [testimonialsRef, testimonialsVisible] = useScrollAnimation();
  const [blogsRef, blogsVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();
  const [learnMoreExpanded, setLearnMoreExpanded] = useState(false);
  const [blogPosts, setBlogPosts] = useState<PublicBlog[]>([]);
  const [testimonials, setTestimonials] = useState<PublicTestimonial[]>([]);

  // Helper to check if a post is featured (handles both boolean and number)
  const isFeatured = (post: PublicBlog) => {
    const f = post.featured;
    return f === true || f === 1 || String(f) === '1';
  };

  useEffect(() => {
    fetchBlogs();
    fetchTestimonials();
  }, []);

  const fetchBlogs = async () => {
    try {
      // Get all published blogs
      const data = await blogService.getPublishedBlogs({
        page: 1,
        limit: 10, // Fetch more to ensure we have enough to sort
      });

      // Sort: Featured first, then by date (newest first)
      const sortedBlogs = data.blogs.sort((a, b) => {
        const aFeatured = isFeatured(a);
        const bFeatured = isFeatured(b);

        if (aFeatured && !bFeatured) return -1;
        if (!aFeatured && bFeatured) return 1;

        // If both same featured status, sort by date
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      // Set max 3 blogs
      setBlogPosts(sortedBlogs.slice(0, 3));
    } catch (error: any) {
      console.error('Failed to fetch blogs:', error);
      setBlogPosts([]);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const data = await testimonialService.getAllTestimonials();
      // Sort: Featured first, limit to 3
      const sorted = data.testimonials.sort((a, b) => {
        const aFeatured = Boolean(a.featured);
        const bFeatured = Boolean(b.featured);

        if (aFeatured && !bFeatured) return -1;
        if (!aFeatured && bFeatured) return 1;

        // Optional: sub-sort by date if available, or just keeping original order
        return 0;
      }).slice(0, 3);

      setTestimonials(sorted);
    } catch (error: any) {
      console.error('Failed to fetch testimonials:', error);
      setTestimonials([]);
    }
  };

  // Helper to render stars for rating
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };





  return (
    <div className="min-h-screen bg-[#faf8ed] selection:bg-[#0c513f] selection:text-[#faf8ed]">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-52 pb-40 px-4 min-h-[90vh] flex items-center overflow-hidden relative">
        <div className="absolute inset-0 z-0 scale-100 origin-bottom">
          <img
            src="/hero-img.png"
            alt="Hero Background"
            className="w-full h-full object-cover object-bottom animate-ken-burns opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#faf8ed] via-[#faf8ed]/60 to-transparent"></div>
        </div>

        <div className="container-premium text-center relative z-10">
          <div
            ref={heroRef}
            className={`transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
          >
            <div className="max-w-5xl mx-auto space-y-12">
              <h1 className="heading-huge text-[#0c513f] tracking-tightest-premium drop-shadow-sm mb-6">
                Universally <br /> Simple QR Payments.
              </h1>
              <p className="text-2xl md:text-3xl text-secondary-900 leading-tight max-w-2xl mx-auto font-bold tracking-tight">
                Discover businesses. Navigate easily. <br />
                Pay instantly. The future of commerce is here.
              </p>

              {/* App Buttons Row */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-12">
                <Link
                  to="/services"
                  className="btn-chowdeck text-xl px-12 py-6 min-w-[240px] z-10 shadow-[0_20px_50px_rgba(12,81,63,0.3)]"
                >
                  Start Exploring
                </Link>
                <div className="flex gap-4 z-10">
                  <a href="#" className="bg-black text-white px-6 py-3 rounded-xl flex items-center gap-3 hover:scale-105 transition-all shadow-xl">
                    <div className="text-right">
                      <p className="text-[10px] uppercase font-bold leading-none">Download on the</p>
                      <p className="text-lg font-black leading-none">App Store</p>
                    </div>
                  </a>
                  <a href="#" className="bg-black text-white px-6 py-3 rounded-xl flex items-center gap-3 hover:scale-105 transition-all shadow-xl">
                    <div className="text-right">
                      <p className="text-[10px] uppercase font-bold leading-none">Get it on</p>
                      <p className="text-lg font-black leading-none">Google Play</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* New Dynamic Hero Background Elements - Moved out of container for full width */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden -z-10">
          {/* Animated Blobs for depth */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-chowdeck-pastelYellow rounded-full blur-[120px] opacity-40 animate-blob"></div>
          <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-chowdeck-pastelPurple rounded-full blur-[150px] opacity-40 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-blue-100 rounded-full blur-[100px] opacity-30 animate-blob animation-delay-4000"></div>

          {/* Floating Project Icons */}
          <div className="absolute top-1/4 left-[10%] animate-float transition-all duration-1000 opacity-20">
            <MapPin size={120} className="text-[#0c513f] rotate-12" />
          </div>
          <div className="absolute top-1/3 right-[10%] animate-float animation-delay-2000 opacity-20">
            <Wallet size={150} className="text-[#0c513f] -rotate-12" />
          </div>
          <div className="absolute bottom-1/4 left-[15%] animate-float animation-delay-4000 opacity-20">
            <Navigation size={100} className="text-[#0c513f] rotate-45" />
          </div>
          <div className="absolute bottom-[10%] right-[20%] animate-float animation-delay-2000 opacity-20">
            <Store size={130} className="text-[#0c513f]" />
          </div>
        </div>
      </section>

      {/* Infinite Marquee Section */}
      <div className="bg-[#0c513f] py-8 overflow-hidden relative z-30 transform -rotate-2 scale-105">
        <div className="marquee-container flex">
          <div className="marquee-content animate-marquee whitespace-nowrap flex items-center">
            {[...Array(4)].map((_, i) => (
              <React.Fragment key={i}>
                <span className="text-white text-3xl font-black mx-12 flex items-center gap-4">
                  <Zap className="text-yellow-400" fill="currentColor" /> FAST PAYMENTS
                </span>
                <span className="text-white text-3xl font-black mx-12 flex items-center gap-4">
                  <Store className="text-blue-400" /> DISCOVER BUSINESSES
                </span>
                <span className="text-white text-3xl font-black mx-12 flex items-center gap-4">
                  <Shield className="text-green-400" /> SECURE KYC
                </span>
                <span className="text-white text-3xl font-black mx-12 flex items-center gap-4">
                  <Star className="text-pink-400" fill="currentColor" /> TOP RATED
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Premium Horizontal Accordion Features */}
      <section ref={featuresRef} className="py-32 px-4 bg-white rounded-chow-lg -mt-16 relative z-20">
        <div className="container-premium">
          <div className={`mb-20 transition-all duration-1000 ${featuresVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <h2 className="text-6xl md:text-8xl font-black text-[#0c513f] mb-8 tracking-tighter-premium leading-none">
              One platform. <br /> Endless discovery.
            </h2>
            <p className="text-2xl text-secondary-600 max-w-2xl font-medium tracking-tight">
              Whatever you need, find it and pay for it with absolute ease.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row h-auto lg:h-[600px] gap-4">
            {[
              {
                title: 'Restaurants',
                desc: 'From street food to fine dining, scan and eat.',
                icon: <Store size={60} />,
                color: 'bg-[#ffedcc]',
                img: '🍔'
              },
              {
                title: 'Groceries',
                desc: 'Stock up your home with a single QR scan.',
                icon: <Zap size={60} />,
                color: 'bg-[#e0f2fe]',
                img: '🛒'
              },
              {
                title: 'Health',
                desc: 'Medication and wellness at your fingertips.',
                icon: <Shield size={60} />,
                color: 'bg-[#f3e8ff]',
                img: '💊'
              },
              {
                title: 'Analytics',
                desc: 'Track your spending or your business growth.',
                icon: <BarChart3 size={60} />,
                color: 'bg-[#f0fdf4]',
                img: '📈'
              }
            ].map((item, i) => (
              <div
                key={i}
                className={`${item.color} group relative overflow-hidden flex-1 hover:flex-[3] transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] rounded-chow p-6 lg:p-12 flex flex-col justify-between`}
              >
                <div className="flex justify-between items-start">
                  <div className="bg-white/50 backdrop-blur-sm p-6 rounded-3xl text-[#0c513f]">
                    {item.icon}
                  </div>
                  <span className="text-8xl opacity-20 grayscale group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-500">
                    {item.img}
                  </span>
                </div>

                <div className="mt-auto">
                  <h3 className="text-4xl font-black text-[#0c513f] mb-4 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 translate-y-0 lg:translate-y-10 lg:group-hover:translate-y-0 transition-all duration-500 delay-100">
                    {item.title}
                  </h3>
                  <p className="text-xl text-[#0c513f]/70 font-bold opacity-100 lg:opacity-0 lg:group-hover:opacity-100 translate-y-0 lg:translate-y-10 lg:group-hover:translate-y-0 transition-all duration-500 delay-200">
                    {item.desc}
                  </p>

                  {/* Vertical Label when collapsed */}
                  <div className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 group-hover:opacity-0 transition-opacity duration-300">
                    <span className="text-3xl font-black text-[#0c513f] whitespace-nowrap -rotate-90 block">
                      {item.title}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section ref={stepsRef} className="py-40 px-4 bg-white">
        <div className="container-premium">
          <div className={`mb-24 transition-all duration-1000 ${stepsVisible ? 'opacity-100' : 'opacity-0'}`}>
            <h2 className="text-6xl md:text-7xl font-black text-[#0c513f] mb-8 tracking-tighter-premium">
              Getting started <br /> is as easy as ABC.
            </h2>
            <p className="text-2xl text-secondary-600 max-w-2xl font-medium">
              Three simple steps to unlock a new world of commerce.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Discover',
                description: 'Open the app and find everything around you. From hot meals to fuel stations.',
                color: 'bg-[#fff9e6]'
              },
              {
                step: '02',
                title: 'Navigate',
                description: 'Let us guide you to your destination with precise real-time navigation.',
                color: 'bg-[#f0fdf4]'
              },
              {
                step: '03',
                title: 'Pay Seamlessly',
                description: 'One scan, one click. Payment done. No more "Do you have change?".',
                color: 'bg-[#e0f2fe]'
              }
            ].map((item, index) => (
              <div
                key={index}
                className={`${item.color} p-12 rounded-[3.5rem] relative group hover:scale-[1.02] transition-all duration-500 overflow-hidden h-[400px] flex flex-col justify-end border-2 border-transparent hover:border-[#0c513f11] shadow-sm hover:shadow-xl ${stepsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
                  }`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <div className="absolute top-0 right-0 p-12 text-9xl font-black text-[#0c513f] opacity-5 group-hover:opacity-10 transition-opacity">
                  {item.step}
                </div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-4xl mb-8 shadow-sm">
                    {item.step === '01' ? '📍' : item.step === '02' ? '🚶' : '⚡'}
                  </div>
                  <h3 className="text-4xl font-black text-[#0c513f] mb-4 tracking-tight">
                    {item.title}
                  </h3>
                  <p className="text-xl text-secondary-600 font-medium leading-tight">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section ref={testimonialsRef} className="py-24 px-4 bg-[#faf8ed]">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${testimonialsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
            }`}>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0c513f] mb-6 tracking-tight">
              Customer Love
            </h2>
            <p className="text-xl md:text-2xl text-secondary-600 font-medium">
              Real feedback from people who trust Rita
            </p>
          </div>

          {testimonials.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16">
                {testimonials.map((testimonial, index) => (
                  <div
                    key={testimonial.id}
                    className={`bg-white rounded-[2.5rem] p-10 shadow-sm hover:shadow-xl transition-all duration-500 group ${testimonialsVisible
                      ? `animate-fade-in-up opacity-100`
                      : 'opacity-0'
                      }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-center space-x-4 mb-8">
                      {testimonial.profile ? (
                        <img
                          src={getImageUrl(testimonial.profile) || testimonial.profile}
                          alt={testimonial.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-[#0c513f]/10"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-[#0c513f] flex items-center justify-center text-white font-bold text-xl">
                          {testimonial.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h4 className="text-xl font-bold text-[#0c513f]">{testimonial.name}</h4>
                        {testimonial.location && (
                          <p className="text-secondary-500 font-medium">{testimonial.location}</p>
                        )}
                      </div>
                    </div>

                    <p className="text-secondary-700 text-lg leading-relaxed mb-6 italic">
                      "{testimonial.text}"
                    </p>

                    <div className="flex items-center justify-between pt-6 border-t border-secondary-50">
                      {renderStars(testimonial.rating)}
                      {Boolean(testimonial.verified) && (
                        <span className="text-[#0c513f] text-sm font-bold bg-[#0c513f]/5 px-3 py-1 rounded-full">
                          Verified User
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <Link
                  to="/testimonials"
                  className="btn-chowdeck"
                >
                  View All Testimonials
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-secondary-600 text-xl mb-8">No testimonials available yet.</p>
              <Link to="/testimonials" className="btn-chowdeck">
                View Page
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Blogs Section */}
      <section ref={blogsRef} className="py-24 px-4 bg-white rounded-[4rem]">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${blogsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
            }`}>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0c513f] mb-6 tracking-tight">
              Rita Stories
            </h2>
            <p className="text-xl md:text-2xl text-secondary-600 font-medium">
              Stay informed with insights and tips
            </p>
          </div>

          {blogPosts.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-10 mb-16">
              {blogPosts.map((post, index) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className={`group block transition-all duration-500 ${blogsVisible
                    ? `animate-fade-in-up opacity-100`
                    : 'opacity-0'
                    }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="rounded-[2.5rem] overflow-hidden mb-6 h-72 relative">
                    {post.image ? (
                      <img
                        src={getImageUrl(post.image) || post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full bg-chowdeck-pastelYellow flex items-center justify-center text-8xl">
                        📝
                      </div>
                    )}
                    <div className="absolute top-6 left-6 flex gap-2">
                      <span className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold text-[#0c513f]">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-3xl font-extrabold text-[#0c513f] mb-4 group-hover:underline decoration-4">
                    {post.title}
                  </h3>
                  <p className="text-secondary-600 text-lg line-clamp-2 font-medium">
                    {post.description}
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-secondary-600 text-xl">No stories found.</p>
            </div>
          )}

          <div className="text-center">
            <Link
              to="/blog"
              className="btn-chowdeck"
            >
              Ready for more?
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-40 px-4 bg-[#0c513f] rounded-chow shadow-2xl mx-4 mb-10 overflow-hidden relative">
        <div className="absolute inset-0 bg-grid-premium opacity-10"></div>
        <div className={`max-w-5xl mx-auto text-center text-white relative z-10 transition-all duration-1000 ${ctaVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}>
          <h2 className="heading-huge mb-10 text-[#faf8ed] tracking-tightest-premium">
            Ready to <br /> jump in?
          </h2>
          <p className="text-2xl md:text-3xl mb-16 text-[#faf8ed]/80 font-medium">
            Join thousands of businesses already using Rita <br /> for seamless payments and discovery.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
            <Link
              to="/contact"
              className="bg-[#faf8ed] text-[#0c513f] px-16 py-8 rounded-[2rem] font-black text-3xl hover:bg-white transition-all duration-300 shadow-2xl hover:scale-110 active:scale-95 min-w-[320px]"
            >
              Get Started
            </Link>

            <div className="flex flex-col gap-4">
              <a href="#" className="border-2 border-[#faf8ed]/20 bg-[#faf8ed]/5 px-8 py-4 rounded-2xl flex items-center gap-4 hover:bg-[#faf8ed]/10 transition-all">
                <div className="text-left">
                  <p className="text-xs font-bold text-[#faf8ed]/60">Download on the</p>
                  <p className="text-xl font-black text-[#faf8ed]">App Store</p>
                </div>
              </a>
              <a href="#" className="border-2 border-[#faf8ed]/20 bg-[#faf8ed]/5 px-8 py-4 rounded-2xl flex items-center gap-4 hover:bg-[#faf8ed]/10 transition-all">
                <div className="text-left">
                  <p className="text-xs font-bold text-[#faf8ed]/60">Get it on</p>
                  <p className="text-xl font-black text-[#faf8ed]">Google Play</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Learn More About Rita - SEO Section */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setLearnMoreExpanded(!learnMoreExpanded)}
              className="bg-secondary-200 hover:bg-secondary-300 text-secondary-800 px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2 shadow-sm hover:shadow-md"
            >
              <span>Learn More About Rita</span>
              {learnMoreExpanded ? (
                <ChevronUp size={20} className="transition-transform" />
              ) : (
                <ChevronDown size={20} className="transition-transform" />
              )}
            </button>
          </div>

          <div
            className={`overflow-hidden transition-all duration-500 ${learnMoreExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            <div className="max-w-4xl mx-auto space-y-8 text-secondary-700">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-6">
                  Rita - A Universal QR Payment & Discovery Platform
                </h2>
                <div className="space-y-4 text-lg leading-relaxed">
                  <p>
                    Rita is a comprehensive digital platform that revolutionizes the way customers discover businesses and make payments.
                    Our universal QR payment and discovery platform offers seamless digital wallet solutions, instant QR code payments,
                    business location services, and real-time navigation. Whether you're looking for nearby stores, restaurants, salons,
                    fuel stations, or service providers, Rita simplifies your journey by combining business discovery, navigation, and
                    payment processing into one intuitive platform.
                  </p>
                  <p>
                    Our mission is to empower businesses of all sizes while providing customers with a smooth, convenient experience.
                    For consumers, Rita offers a seamless way to discover and pay at local businesses without the need for cash or cards.
                    For business owners, we provide cutting-edge point-of-sale systems, comprehensive analytics, and intelligent payment
                    solutions that help grow their customer base and streamline operations. Rita delivers convenience, transparency, and
                    cutting-edge technology through our advanced business management tools, dedicated mobile applications, and secure
                    payment infrastructure.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  Key Features of Rita
                </h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Digital Wallet
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita's secure and intuitive digital wallet allows users to hold funds, make instant payments, and track all
                      transactions in real-time. With our wallet, you can easily manage your finances, view transaction history,
                      and enjoy the convenience of scanning QR codes instead of carrying cash or cards. The wallet is protected
                      by advanced encryption and security measures to ensure your money and personal information remain safe.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      QR Code Payment System
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Our innovative QR code payment system enables instant, contactless transactions at any business partner.
                      Simply scan the QR code displayed at the business location, confirm the amount, and complete your payment
                      securely through your digital wallet. This eliminates the need for physical cash or credit cards, making
                      transactions faster, safer, and more convenient for both customers and businesses.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Business Discovery & Navigation
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita's powerful discovery platform helps you find nearby businesses based on your location, preferences,
                      and search criteria. Our integrated mapping system provides real-time navigation, directions, and business
                      information including hours, ratings, and available services. Whether you're looking for a specific store,
                      restaurant, or service provider, Rita makes it easy to discover and navigate to your destination.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Business Analytics & Management
                    </h4>
                    <p className="text-lg leading-relaxed">
                      For business owners, Rita offers comprehensive analytics and management tools to track performance, monitor
                      transactions, and gain valuable insights into customer behavior. Our dashboard provides real-time reports,
                      sales analytics, customer demographics, and performance metrics to help you make data-driven decisions and
                      grow your business effectively.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Security & Verification
                    </h4>
                    <p className="text-lg leading-relaxed">
                      All businesses on the Rita platform undergo KYC (Know Your Customer) verification to ensure authenticity
                      and security. Our platform uses industry-standard encryption protocols to protect all transactions and
                      personal data. With Rita, you can trust that every transaction is secure, verified, and protected by
                      advanced security measures.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Multi-Category Support
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita supports businesses across multiple categories including retail stores, restaurants, cafes, salons,
                      fuel stations, service providers, healthcare facilities, and more. Our platform is designed to accommodate
                      various business types and industries, making it a versatile solution for businesses of all sizes and sectors.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  How Rita Benefits You
                </h3>
                <div className="space-y-4 text-lg leading-relaxed">
                  <p>
                    <strong className="text-secondary-900">For Customers:</strong> Rita provides a seamless, cashless payment
                    experience with the convenience of discovering and navigating to nearby businesses. Our platform saves time,
                    reduces the need for physical currency, and offers a secure way to manage your transactions and explore local
                    businesses.
                  </p>
                  <p>
                    <strong className="text-secondary-900">For Businesses:</strong> Rita helps businesses reach more customers,
                    streamline payment processing, and gain valuable insights through our analytics platform. Our tools enable
                    businesses to accept digital payments easily, track performance, and grow their customer base with minimal
                    setup and maintenance.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  Getting Started with Rita
                </h3>
                <p className="text-lg leading-relaxed">
                  Getting started with Rita is simple and straightforward. Download our mobile application, create your account,
                  and set up your digital wallet. For businesses, our onboarding process includes KYC verification, business profile
                  setup, and QR code generation. Our support team is available to assist you throughout the process, ensuring a
                  smooth and hassle-free experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;

